import { inject, Injectable } from "@angular/core";
import { Observable, merge, fromEvent, interval, Subscription, BehaviorSubject, throttle } from "rxjs";
import { TimeoutSettings } from "@app/shared/models/system-settings.model";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";

@Injectable({
  providedIn: "root"
})
export class TimeoutService {
  expired: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _modalSheetService = inject(ModalSheetService);
  private _idle: Observable<any>;
  private _timeOutSeconds: number;
  private _timeOutSettings: TimeoutSettings;

  private _timer: Subscription;
  private _idleSubscription: Subscription;
  private _expiredSubscription: Subscription;

  startWatching(): void {
    // Only start the timer once and if its enabled
    if (this._timer || !this.timeOutSettings.isTimeoutEnabled) {
      return;
    }

    this._timeOutSeconds = this.timeOutSettings.timeoutMinutes * 60 * 1000;
    this._startIdle();
    this._startTimer();
  }

  /**
   * Reset the time if the user is active.
   * If time out dialog open, close it.
   */
  resetTimer() {
    this.expired.next(false);
    this._timer.unsubscribe();
    this._startTimer();
  }

  /**
   * Stop the timer, usually triggered when signing out or closing the browser.
   */
  stopTimer() {
    this._timer?.unsubscribe();
    this._idleSubscription?.unsubscribe();
    this._expiredSubscription?.unsubscribe();
    this._timer = null;
    this.expired.next(false);
  }

  /**
   * Set variable to store timeout settings.
   */
  set timeOutSettings(data: TimeoutSettings) {
    this._timeOutSettings = data;
  }

  /**
   * Return time out settings
   */
  get timeOutSettings() {
    return this._timeOutSettings;
  }

  /**
   * Start watching for movements by user in DOM
   */
  private _startIdle() {
    // If the user does any of the following actions, the timer is reset.
    this._idle = merge(
      fromEvent(document, "mousemove"),
      fromEvent(document, "click"),
      fromEvent(document, "mousedown"),
      fromEvent(document, "keypress"),
      fromEvent(document, "DOMMouseScroll"),
      fromEvent(document, "mousewheel"),
      fromEvent(document, "touchmove"),
      fromEvent(document, "MSPointerMove"),
      fromEvent(window, "mousemove"),
      fromEvent(window, "resize")
    );

    this._idleSubscription = this._idle.pipe(throttle(() => interval(1000))).subscribe(() => {
      this.resetTimer();
    });
  }
  /**
   * Start watching for user inactivity.
   */
  private _startTimer() {
    this._timer = interval(this._timeOutSeconds).subscribe(() => {
      if (this.expired.value) {
        return;
      }

      this.expired.next(true);
      this._modalSheetService.openTimeout().subscribe();
    });
  }
}
